import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

interface FrequencyDisplayProps {
  rx: string;
  tx: string;
  tone?: string;
  offset?: string;
  visualizer?: boolean;
  className?: string;
}

export function FrequencyDisplay({
  rx,
  tx,
  tone,
  offset,
  visualizer = true,
  className = '',
}: FrequencyDisplayProps) {
  const [bars, setBars] = useState<number[]>([]);

  // Generate random bar heights for visualizer animation
  useEffect(() => {
    if (!visualizer) return;

    const generateBars = () => {
      const newBars = Array.from({ length: 12 }, () => Math.random() * 100);
      setBars(newBars);
    };

    generateBars();
    const interval = setInterval(generateBars, 150);
    return () => clearInterval(interval);
  }, [visualizer]);

  return (
    <motion.div
      className={`glass-morphism rounded-lg p-6 ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Visualizer bars */}
      {visualizer && (
        <div className="flex items-end justify-center gap-1 h-8 mb-4 overflow-hidden">
          {bars.map((height, index) => (
            <motion.div
              key={index}
              className="w-1 bg-gradient-to-t from-accent-primary to-accent-secondary rounded-full"
              initial={{ height: 4 }}
              animate={{ height: `${Math.max(4, height * 0.3)}px` }}
              transition={{ duration: 0.1, ease: "easeOut" }}
            />
          ))}
        </div>
      )}

      {/* Frequency display */}
      <div className="space-y-4">
        {/* RX Frequency */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-text-secondary uppercase tracking-wider">
            RX
          </span>
          <motion.span
            className="font-mono text-2xl font-bold text-accent-primary"
            style={{ textShadow: '0 0 10px rgba(88, 166, 255, 0.5)' }}
            whileHover={{ scale: 1.05 }}
          >
            {rx}
          </motion.span>
        </div>

        {/* TX Frequency */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-text-secondary uppercase tracking-wider">
            TX
          </span>
          <motion.span
            className="font-mono text-2xl font-bold text-accent-secondary"
            style={{ textShadow: '0 0 10px rgba(31, 111, 235, 0.5)' }}
            whileHover={{ scale: 1.05 }}
          >
            {tx}
          </motion.span>
        </div>

        {/* Optional tone and offset */}
        {(tone || offset) && (
          <div className="pt-3 border-t border-border-primary">
            <div className="flex items-center justify-between text-sm">
              {tone && (
                <div className="flex items-center gap-2">
                  <span className="text-text-secondary">Tone:</span>
                  <span className="font-mono text-text-primary font-semibold">{tone}</span>
                </div>
              )}
              {offset && (
                <div className="flex items-center gap-2">
                  <span className="text-text-secondary">Offset:</span>
                  <span className="font-mono text-text-primary font-semibold">{offset}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Frequency glow effect */}
      <motion.div
        className="absolute -inset-1 bg-gradient-to-r from-accent-primary/10 to-accent-secondary/10 rounded-lg blur-xl -z-10"
        animate={{
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </motion.div>
  );
}
